<script setup>
import { useField, useFormContext } from 'vee-validate'
import { computed } from 'vue'

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },
  type: {
    type: String,
    default: 'text',
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  info: {
    type: String,
    default: '',
  },
})

const model = defineModel({ type: [String, Number] })

// Validate
const { value, errorMessage } = useField(() => props.name, undefined, {
  syncVModel: true,
})

// Acessa o schema do formulário pai via useFormContext — undefined quando input está solto
const schema = useFormContext()?.schema

// Usa o describe() do Yup para verificar se o campo tem a regra required
const isRequired = computed(() => {
  try {
    const fieldSchema = schema.value?.describe?.()?.fields?.[props.name]
    return fieldSchema?.tests?.some(t => t.name === 'required') ?? false
  } catch {
    return false
  }
})
</script>

<template>
  <div
    class="flex flex-col gap-1 px-0.5 py-0"
    :class="{
      'text-neutral-40': disabled,
    }"
  >
    <div class="flex items-center gap-2">
      <label
        class="text-neutral-60"
        :class="{
          'text-base': size === 'lg',
          'text-sm': size === 'md',
          'text-xs': size === 'sm',
        }"
        :for="name"
      >
        {{ label }}
        <span v-if="isRequired" class="text-error-60">*</span>
      </label>
      <ImoIcon v-tooltip="{ text: info }" v-if="info" name="info-circle" :size="14" />
    </div>
    <div class="flex flex-row">
      <input
        class="placeholder:text-neutral-40 w-full rounded-lg"
        :class="{
          error: errorMessage,
          input: !errorMessage,
          'h-[38px]': size === 'md',
          'h-[44px]': size === 'lg',
          'h-[36px]': size === 'sm',
          'border-neutral-30': !model || model.length === 0,
          'border-neutral-40': model && model.length > 0,
        }"
        v-bind="$attrs"
        :id="$attrs.id"
        :disabled="disabled"
        :type="type"
        :placeholder="placeholder"
        v-model="value"
      />
    </div>
    <span class="text-error-60 text-xs" v-html="errorMessage"></span>
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.input {
  @apply disabled:bg-neutral-10 disabled:border-neutral-30 disabled:text-neutral-40 text-primary-90;
}

.input:focus-visible {
  @apply border-[#4c94ff73];
  box-shadow: 0px 0px 0px 3px #4c94ff73;
}

.error {
  @apply bg-error-10 border-error-60 text-error-60;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
